import React, { useState, useEffect } from "react";
import { Button } from "../../ui";
import { useParams, useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody } from "mdb-react-ui-kit";
import NativeInput from "../../NativeInput";
import { api } from "../../../services";
import { useTranslation } from "react-i18next";

function AdminAddRoutes() {
  const { t } = useTranslation("translation");
  const { appId } = useParams(); // Get the app ID from the URL
  const [routes, setRoutes] = useState([]);
  const [routeForm, setRouteForm] = useState({
    name: "",
    method: "GET",
    endpoint: "",
    headers: "{}",
    queryParams: "{}",
    bodyTemplate: "{}",
  });
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoutes();
  }, []);

  // Fetch existing routes for the app
  const fetchRoutes = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.get(`/admin/available-apps/${appId}/routes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoutes(response.data.routes || []);
    } catch (error) {
      console.error("Error fetching routes:", error.message);
    }
  };

  // Handle form input changes for non-JSON fields
  const handleRouteFormChange = (e) => {
    const { name, value } = e.target;
    setRouteForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle JSON field changes
  const handleJsonFieldChange = (field, value) => {
    setRouteForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(""); // Reset error if any
  };

  // Validate and parse JSON before submitting
  const validateAndParseJSON = (jsonString, fieldName) => {
    try {
      return JSON.parse(jsonString || "{}");
    } catch (err) {
      setError(`${t("Invalid JSON format in")} ${fieldName}.`);
      throw new Error(`${t("Invalid JSON format in")} ${fieldName}`);
    }
  };

  // Submit new or updated route
  const handleAddOrUpdateRoute = async () => {
    if (!routeForm.name || !routeForm.method || !routeForm.endpoint) {
      setError(`${t("Route Name, Method, and Endpoint are required.")}`);
      return;
    }

    try {
      const headers = validateAndParseJSON(routeForm.headers, "Headers");
      const queryParams = validateAndParseJSON(
        routeForm.queryParams,
        "Query Parameters"
      );
      const bodyTemplate = validateAndParseJSON(
        routeForm.bodyTemplate,
        "Body Template"
      );

      const token = localStorage.getItem("adminToken");
      await api.post(
        `/admin/available-apps/${appId}/routes`,
        {
          ...routeForm,
          headers,
          queryParams,
          bodyTemplate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccessMessage(`${t("Route added/updated successfully!")}`);
      setError("");
      setRouteForm({
        name: "",
        method: "GET",
        endpoint: "",
        headers: "{}",
        queryParams: "{}",
        bodyTemplate: "{}",
      });
      fetchRoutes(); // Refresh the list of routes
    } catch (error) {
      console.log("Error adding/updating route:", error.message);
      setError(`${t("Failed to add/update route. Please try again.")}`);
    }
  };

  // Delete a route
  const handleDeleteRoute = async (routeId) => {
    if (
      !window.confirm(`${t("Are you sure you want to delete this route?")}`)
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminLogin");
      await api.delete(`/admin/available-apps/${appId}/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchRoutes(); // Refresh the list of routes
    } catch (error) {
      console.error("Error deleting route:", error.message);
    }
  };

  return (
    <MDBContainer fluid className="p-4 content-area flex-grow-1">
      <h2>{t("Manage Routes for App")}</h2>

      {/* Add/Update Route Form */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <h5>{t("Add or Update Route")}</h5>
          <NativeInput
            label={t("Route Name")}
            name="name"
            value={routeForm.name}
            onChange={handleRouteFormChange}
            className="mb-3"
            required
          />
          <select
            name="method"
            value={routeForm.method}
            onChange={handleRouteFormChange}
            className="form-select mb-3"
            required
          >
            <option value="GET">{t("GET")}</option>
            <option value="POST">{t("POST")}</option>
            <option value="PUT">{t("PUT")}</option>
            <option value="DELETE">{t("delete")}</option>
          </select>
          <NativeInput
            label={t("Endpoint")}
            name="endpoint"
            value={routeForm.endpoint}
            onChange={handleRouteFormChange}
            className="mb-3"
            required
          />
          <NativeInput
            label={t("Headers (JSON)")}
            name="headers"
            value={routeForm.headers}
            onChange={(e) => handleJsonFieldChange("headers", e.target.value)}
            className="mb-3"
          />
          <NativeInput
            label={t("Query Parameters (JSON)")}
            name="queryParams"
            value={routeForm.queryParams}
            onChange={(e) =>
              handleJsonFieldChange("queryParams", e.target.value)
            }
            className="mb-3"
          />
          <NativeInput
            label={t("Body Template (JSON)")}
            name="bodyTemplate"
            value={routeForm.bodyTemplate}
            onChange={(e) =>
              handleJsonFieldChange("bodyTemplate", e.target.value)
            }
            className="mb-3"
          />
          {error && <p className="text-danger">{error}</p>}
          {successMessage && <p className="text-success">{successMessage}</p>}
          <Button color="primary" onClick={handleAddOrUpdateRoute}>
            {t("Save Route")}
          </Button>
        </MDBCardBody>
      </MDBCard>

      {/* List of Existing Routes */}
      <h5>{t("Existing Routes")}</h5>
      <MDBRow>
        {routes.map((route) => (
          <MDBCol md="6" key={route._id} className="mb-4">
            <MDBCard className="h-100">
              <MDBCardBody>
                <p>
                  <strong>{t("name")}:</strong> {route.name}
                </p>
                <p>
                  <strong>{t("Method")}:</strong> {t(route.method)}
                </p>
                <p>
                  <strong>{t("Endpoint")}:</strong> {route.endpoint}
                </p>
                <div className="d-flex justify-content-between">
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    onClick={() => handleDeleteRoute(route._id)}
                  >
                    {t("delete")}
                  </Button>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        ))}
      </MDBRow>
    </MDBContainer>
  );
}

export default AdminAddRoutes;
